import { useEffect, useState, useCallback, useRef } from "react";
import Pagination from "@mui/material/Pagination";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomSelect from "../../../../components/CustomSelect";
import moment from "moment";
// @ts-ignore
import html2pdf from "html2pdf.js";

// ─── Type Definitions ─────────────────────────────────────────────────────────
interface Municipality {
    primary_id: string;
    municipality_name: string;
}
interface Borough {
    borough_primary_id: string;
    borough_name: string;
}
interface Ward {
    ward_primary_id: string;
    ward_no: string;
}
interface Street {
    street_primary_id: string;
    street_name: string;
}
interface LedgerItem {
    id: number;
    connection_number: string;
    consumer_name: string;
    consumer_category_name?: string | null;
    consumer_monthly_rate?: number | null;
    opening_balance_left_to_pay: number;
    amount_deposited: number;
    total_due_amount: number;
    due_months: any[];
    advance_amount: number | null;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────
const getToday = () => new Date().toISOString().split("T")[0];
const getOneMonthAgo = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
};
const formatDateTime = (isoString: string | null) => {
    if (!isoString) return "-";
    const m = moment(isoString);
    if (!m.isValid()) return "-";
    return m.local().format("DD/MM/YYYY, hh:mm A");
};

const getPreviousFinancialYearRange = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0 = Jan, 3 = Apr
    const prevFyStartYear = currentMonth >= 3 ? currentYear - 1 : currentYear - 2;
    return {
        startDate: new Date(prevFyStartYear, 3, 1),
        endDate: new Date(prevFyStartYear + 1, 2, 31, 23, 59, 59)
    };
};

// ─── AllotmentCell (copied from DemandDraft) ─────────────────────────────────
const AllotmentCell = ({ value }: { value: string }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const truncated = value ? `${value.slice(0, 8)}...` : "-";

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
                btnRef.current && !btnRef.current.contains(event.target as Node)
            ) setShowTooltip(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (showTooltip && tooltipRef.current) {
            const rect = tooltipRef.current.getBoundingClientRect();
            const vw = window.innerWidth;
            if (rect.right > vw) {
                tooltipRef.current.style.left = "auto";
                tooltipRef.current.style.right = "0";
                tooltipRef.current.style.transform = "translateX(0)";
            } else if (rect.left < 0) {
                tooltipRef.current.style.left = "0";
                tooltipRef.current.style.transform = "translateX(0)";
            } else {
                tooltipRef.current.style.left = "50%";
                tooltipRef.current.style.right = "auto";
                tooltipRef.current.style.transform = "translateX(-30%)";
            }
        }
    }, [showTooltip]);

    return (
        <div className="flex items-center gap-1.5">
            <span className="font-mono text-sm text-gray-600">{truncated}</span>
            <div className="relative inline-block">
                <button
                    ref={btnRef}
                    onClick={(e) => { e.stopPropagation(); setShowTooltip((prev) => !prev); }}
                    className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center hover:bg-blue-200 transition-colors focus:outline-none flex-shrink-0"
                >
                    i
                </button>
                {showTooltip && (
                    <>
                        <div
                            ref={tooltipRef}
                            className="absolute z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl"
                            style={{
                                bottom: "100%", left: "50%", transform: "translateX(-50%)",
                                marginBottom: "10px", minWidth: "max-content",
                                maxWidth: "min(90vw, 400px)", width: "auto",
                                whiteSpace: "normal", wordBreak: "break-word",
                            }}
                        >
                            <div className="px-3 py-2">
                                <p className="font-semibold text-gray-300 mb-1 text-[10px] uppercase tracking-wide text-center">Full Allotment Number</p>
                                <p className="font-mono text-xs text-center break-all">{value}</p>
                            </div>
                        </div>
                        <div
                            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }}
                            onClick={() => setShowTooltip(false)}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const CustomerLedger = () => {
    // ─── Location State ─────────────────────────────────────────────────────
    const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
    const [boroughs, setBoroughs] = useState<Borough[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);
    const [streets, setStreets] = useState<Street[]>([]);

    const [selectedMunicipality, setSelectedMunicipality] = useState<string>("");
    const [selectedBorough, setSelectedBorough] = useState<string>("");
    const [selectedWard, setSelectedWard] = useState<string>("");
    const [selectedStreet, setSelectedStreet] = useState<string>("");

    const [loadingBorough, setLoadingBorough] = useState(false);
    const [loadingWard, setLoadingWard] = useState(false);
    const [loadingStreet, setLoadingStreet] = useState(false);

    const roleType = localStorage.getItem("role_type");
    const municipalityIdFromStorage = localStorage.getItem("municipality_id_v2");
    const municipalityNameFromStorage = localStorage.getItem("municipality_name_v2");
    const isMunicipalityAdmin = roleType === "Municipality Admin";

    // ─── Ledger Data State ───────────────────────────────────────────────────
    const [ledgerData, setLedgerData] = useState<LedgerItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loadingTable, setLoadingTable] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [startDate, setStartDate] = useState(getOneMonthAgo());
    const [endDate, setEndDate] = useState(getToday());
    const [dateError, setDateError] = useState("");

    // ─── Selection ──────────────────────────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);

    // ─── Generate loading (prevent multiple clicks) ──────────────────────────
    const [loadingGenerateSelected, setLoadingGenerateSelected] = useState(false);
    const [loadingGenerateAll, setLoadingGenerateAll] = useState(false);

    // ─── Progress Popup ──────────────────────────────────────────────────────
    const [isProgressOpen, setIsProgressOpen] = useState(false);
    const [reportStatusList, setReportStatusList] = useState<any[]>([]);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusPage, setStatusPage] = useState(1);
    const [statusLimit] = useState(10);
    const [statusTotal, setStatusTotal] = useState(0);
    const [statusLastPage, setStatusLastPage] = useState(1);
    const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

    // ─── Due Months Popup ────────────────────────────────────────────────────
    const [selectedDueMonths, setSelectedDueMonths] = useState<any[]>([]);
    const [openDuePopup, setOpenDuePopup] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

    // ─── Ledger Timeline Popup ────────────────────────────────────────────────
    const [openTimelinePopup, setOpenTimelinePopup] = useState(false);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineData, setTimelineData] = useState<any>(null);
    const [ledgerRows, setLedgerRows] = useState<any[]>([]);

    const limit = 50;
    const hasFetchedOnMount = useRef(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [cooldownSelected, setCooldownSelected] = useState(false);
const [cooldownAll, setCooldownAll] = useState(false);

    // ─── Fetch Ledger ────────────────────────────────────────────────────────
    const fetchLedgerData = useCallback(
        async (pageNum: number, searchValue: string, start: string, end: string) => {
            let mId = selectedMunicipality;
            if (!mId && isMunicipalityAdmin && municipalityIdFromStorage) mId = String(municipalityIdFromStorage);
            if (!selectedStreet || !mId) return;
            setLoadingTable(true);
            try {
                const res = await axiosInstance.post(API_URLS.customerLedger, {
                    page: pageNum, limit,
                    search: searchValue,
                    start_date: start, end_date: end,
                    municipality_id: Number(mId),
                    ward_id: Number(selectedWard),
                    borough_id: Number(selectedBorough),
                    street_id: Number(selectedStreet),
                });
                setLedgerData(res.data?.data?.rows || []);
                setTotalPages(res.data?.data?.pagination?.lastPage || 1);
                setTotalRecords(res.data?.data?.pagination?.total || 0);
            } catch (err: any) {
                toast.error(err?.response?.data?.message || "Failed to fetch ledger data");
            } finally {
                setLoadingTable(false);
            }
        },
        [selectedStreet, selectedMunicipality, isMunicipalityAdmin, municipalityIdFromStorage, selectedWard, selectedBorough, limit]
    );

    useEffect(() => {
        let mId = selectedMunicipality;
        if (!mId && isMunicipalityAdmin && municipalityIdFromStorage) mId = String(municipalityIdFromStorage);
        if (!selectedStreet || !mId) return;
        if (!hasFetchedOnMount.current) hasFetchedOnMount.current = true;
        fetchLedgerData(page, debouncedSearch, startDate, endDate);
    }, [selectedStreet, debouncedSearch, startDate, endDate, page, fetchLedgerData]);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => { setDebouncedSearch(searchTerm); setPage(1); }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchTerm]);

    // ─── Date handlers ───────────────────────────────────────────────────────
    const handleStartDateChange = (val: string) => {
        const today = getToday();
        if (val > today) { setDateError("Start date cannot be a future date."); return; }
        setStartDate(val);
        let newEnd = endDate;
        if (endDate <= val) {
            const d = new Date(val); d.setDate(d.getDate() + 1);
            newEnd = d.toISOString().split("T")[0];
            if (newEnd > today) newEnd = today;
            setEndDate(newEnd);
        }
        setDateError(""); setPage(1);
        fetchLedgerData(1, debouncedSearch, val, newEnd);
    };
    const handleEndDateChange = (val: string) => {
        const today = getToday();
        if (val > today) { setDateError("End date cannot be a future date."); return; }
        if (val < startDate) { setDateError("End date cannot be before start date."); setEndDate(val); return; }
        setEndDate(val); setDateError(""); setPage(1);
        fetchLedgerData(1, debouncedSearch, startDate, val);
    };
    const handleReset = () => {
        const s = getOneMonthAgo(); const e = getToday();
        setStartDate(s); setEndDate(e); setDateError(""); setPage(1);
        fetchLedgerData(1, debouncedSearch, s, e);
    };

    // ─── Location cascade ────────────────────────────────────────────────────
    const fetchBoroughs = async (municipalityId: number) => {
        setLoadingBorough(true);
        setBoroughs([]); setSelectedBorough("");
        setWards([]); setSelectedWard("");
        setStreets([]); setSelectedStreet("");
        setLedgerData([]); setSelectedIds([]);
        try {
            const res = await axiosInstance.post(API_URLS.GetMunicipalityBorough, { municipalityTbl_id: municipalityId, page: 1, limit: 50, surveyer_id: 0 });
            setBoroughs(res.data?.data?.data || []);
        } catch { toast.error("Failed to fetch boroughs"); }
        finally { setLoadingBorough(false); }
    };
    const fetchWards = async (municipalityId: number, boroughId: string) => {
        setLoadingWard(true);
        setWards([]); setSelectedWard("");
        setStreets([]); setSelectedStreet("");
        setLedgerData([]); setSelectedIds([]);
        try {
            const res = await axiosInstance.post(API_URLS.GetBoroughWiseWard, { municipalityTbl_id: municipalityId, borough_primary_id: String(boroughId), page: 1, limit: 50, surveyer_id: 0 });
            setWards(res.data?.data?.data || []);
        } catch { toast.error("Failed to fetch wards"); }
        finally { setLoadingWard(false); }
    };
    const fetchStreets = async (municipalityId: number, wardId: string) => {
        setLoadingStreet(true);
        setStreets([]); setSelectedStreet("");
        setLedgerData([]); setSelectedIds([]);
        try {
            const res = await axiosInstance.post(API_URLS.streetWordWise, { municipalityTbl_id: municipalityId, ward_primary_id: String(wardId), page: 1, limit: 50, surveyer_id: 0 });
            setStreets(res.data?.data?.data || []);
        } catch { toast.error("Failed to fetch streets"); }
        finally { setLoadingStreet(false); }
    };

    const handleMunicipalityChange = (e: any) => {
        const val = e.target.value; setSelectedMunicipality(val);
        if (val) fetchBoroughs(Number(val));
    };
    const handleBoroughChange = (e: any) => {
        const val = e.target.value; setSelectedBorough(val);
        if (val && selectedMunicipality) fetchWards(Number(selectedMunicipality), val);
    };
    const handleWardChange = (e: any) => {
        const val = e.target.value; setSelectedWard(val);
        if (val && selectedMunicipality) fetchStreets(Number(selectedMunicipality), val);
    };
    const handleStreetChange = (e: any) => { setSelectedStreet(e.target.value); };

    // ─── Selection helpers ───────────────────────────────────────────────────
    const toggleSelect = (id: number) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
    };
    const toggleSelectAll = () => {
        const currentPageIds = ledgerData.map((item) => item.id);
        const allSelected = currentPageIds.every((id) => selectedIds.includes(id));
        if (allSelected) setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
        else { const newIds = currentPageIds.filter((id) => !selectedIds.includes(id)); setSelectedIds((prev) => [...prev, ...newIds]); }
    };
    useEffect(() => {
        if (ledgerData.length > 0) {
            const currentPageIds = ledgerData.map((item) => item.id);
            setSelectAllChecked(currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id)));
        } else setSelectAllChecked(false);
    }, [selectedIds, ledgerData]);

    const selectedCount = selectedIds.length;

    const isLocationReady = (): boolean => {
        let mId = selectedMunicipality;
        if (!mId && isMunicipalityAdmin && municipalityIdFromStorage) mId = String(municipalityIdFromStorage);
        return !!(mId && selectedBorough && selectedWard && selectedStreet);
    };
    const validateLocation = (): boolean => {
        if (!isLocationReady()) { toast.error("Please select Municipality, Borough, Ward, and Street first."); return false; }
        return true;
    };

    // ─── Generate Report (with multiple-click prevention) ────────────────────
    const generateSignalReportForIds = async (ids: number[], toastMessage: string) => {
        if (!validateLocation()) return false;
        let mId = selectedMunicipality;
        if (!mId && isMunicipalityAdmin && municipalityIdFromStorage) mId = String(municipalityIdFromStorage);
        const payload = { id: ids, municipality_id: Number(mId), ward_id: Number(selectedWard), borough_id: Number(selectedBorough), street_id: Number(selectedStreet) };
        const loadingToast = toast.loading(toastMessage, { position: "top-right" });
        try {
            const res = await axiosInstance.post(API_URLS.generateSignalReport, payload);
            toast.update(loadingToast, { render: res.data?.message || "Report generation started.", type: "success", isLoading: false, autoClose: 3000 });
            return true;
        } catch (err: any) {
            toast.update(loadingToast, { render: err?.response?.data?.message || "Failed to generate report.", type: "error", isLoading: false, autoClose: 5000 });
            return false;
        }
    };

 const generateReport = async (type: "selected" | "all") => {
    if (!validateLocation()) return;
    if (type === "selected") {
        if (selectedCount === 0) { toast.warning("No rows selected."); return; }
        if (loadingGenerateSelected || cooldownSelected) return;
        setLoadingGenerateSelected(true);
        try {
            const success = await generateSignalReportForIds(selectedIds, "Generating report...");
            if (success) { setSelectedIds([]); setSelectAllChecked(false); }
        } finally {
            setLoadingGenerateSelected(false);
            setCooldownSelected(true);
            setTimeout(() => setCooldownSelected(false), 5000);
        }
    } else {
        if (loadingGenerateAll || cooldownAll) return;
        setLoadingGenerateAll(true);
        try {
            await generateSignalReportForIds([], "Generating all report...");
        } finally {
            setLoadingGenerateAll(false);
            setCooldownAll(true);
            setTimeout(() => setCooldownAll(false), 5000);
        }
    }
};

    // ─── Progress Popup ──────────────────────────────────────────────────────
    const fetchReportStatus = async (pageNum: number = statusPage) => {
        if (!validateLocation()) return;
        let mId = selectedMunicipality;
        if (!mId && isMunicipalityAdmin && municipalityIdFromStorage) mId = String(municipalityIdFromStorage);
        setStatusLoading(true);
        try {
            const res = await axiosInstance.post(API_URLS.generateStatusApi, {
                municipality_id: Number(mId), ward_id: Number(selectedWard),
                borough_id: Number(selectedBorough), street_id: Number(selectedStreet),
                page: pageNum, limit: statusLimit,
            });
            const rows = res.data?.data?.rows || [];
            const pagination = res.data?.data?.pagination;
            setReportStatusList(rows);
            setStatusTotal(Number(pagination?.totalValues || 0));
            setStatusLastPage(pagination?.lastPage || 1);
            setStatusPage(pagination?.page || 1);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to fetch status");
        } finally { setStatusLoading(false); }
    };

    const openProgressPopup = async () => {
        if (!validateLocation()) return;
        setIsProgressOpen(true); setStatusPage(1);
        await fetchReportStatus(1);
    };

    const downloadReport = async (ledgerAllotmentNumber: string) => {
        setDownloadingFile(ledgerAllotmentNumber);
        try {
            const response = await axiosInstance.post(API_URLS.downloadApiReport, { ledger_allotment_number: ledgerAllotmentNumber }, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url; link.setAttribute("download", `ledger_${ledgerAllotmentNumber}.pdf`);
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Download started");
            await fetchReportStatus(statusPage);
        } catch { toast.error("Download failed"); }
        finally { setDownloadingFile(null); }
    };

  
    const handleDueClick = (item: any) => {
        const unpaidMonths = (item.due_months || []).filter((m: any) => !m.is_paid);
        setSelectedDueMonths(unpaidMonths); setSelectedCustomer(item); setOpenDuePopup(true);
    };

    const buildLedgerRows = (data: any) => {
        if (!data) return [];
        const { connection, bills, mappings } = data;
        const rows: any[] = [];

        // 1. Opening Balance
        const initialOpeningDue = Number(connection.opening_due_amount) || 0;
        const { startDate: prevFyStart, endDate: prevFyEnd } = getPreviousFinancialYearRange();
        
        const previousYearUnpaidDues = (bills || [])
            .filter((b: any) => {
                if (b.is_arrear_bill) return false;
                if (b.is_paid) return false;
                const billDate = new Date(b.bill_month);
                return billDate >= prevFyStart && billDate <= prevFyEnd;
            })
            .reduce((acc: number, b: any) => acc + (Number(b.bill_amount) || 0), 0);
            
        const startArrear = initialOpeningDue + previousYearUnpaidDues;
        
        rows.push({
            date: connection.connection_start_date || connection.created_at,
            details: "Opening Balance",
            due: startArrear,
            paid: 0,
            type: "opening"
        });

        // 2. Regular bills (charges)
        (bills || []).forEach((b: any) => {
            if (!b.is_arrear_bill) {
                const billDate = new Date(b.bill_month);
                const isPrevFy = billDate >= prevFyStart && billDate <= prevFyEnd;
                if (isPrevFy && !b.is_paid) return;

                rows.push({
                    date: moment(b.bill_month).add(1, 'months').startOf('month').format('YYYY-MM-DD'),
                    details: `${moment(b.bill_month).format("MMMM YYYY")} Monthly Charges`,
                    due: Number(b.bill_amount) || 0,
                    paid: 0,
                    type: "charge"
                });
            }
        });

        // 3. Payments and adjustments
        (mappings || []).forEach((m: any) => {
            const amtArrear = Number(m.arrear_due_till_march) || 0;
            const amtMonthly = Number(m.monthly_charge_amount) || 0;
            const amtAdvance = Number(m.advance_amount) || 0;
            const date = m.receipt_date || m.created_at;

            if (amtArrear > 0) {
                rows.push({
                    date,
                    details: `Arrear Payment , Receipt No. ${m.receipt_number}`,
                    due: 0,
                    paid: amtArrear,
                    type: "payment"
                });
            }

            if (amtMonthly > 0 && amtAdvance >= 0) {
                // Find corresponding bill month
                const matchedBill = (bills || []).find((b: any) => b.id === m.water_connection_bill_id);
                const billLabel = matchedBill 
                    ? moment(matchedBill.bill_month).format("MMMM YYYY") 
                    : m.receipt_number || "Monthly";

                rows.push({
                    date,
                    details: `${billLabel} Charges Payment Receipt ${m.receipt_number}`,
                    due: 0,
                    paid: amtMonthly,
                    type: "payment"
                });
            }

            if (amtAdvance > 0) {
                rows.push({
                    date,
                    details: `Advance payment`,
                    due: 0,
                    paid: amtAdvance,
                    type: "payment_advance"
                });
            }

            if (amtAdvance < 0) {
                // Adjustment from advance
                const matchedBill = (bills || []).find((b: any) => b.id === m.water_connection_bill_id);
                const billLabel = matchedBill 
                    ? moment(matchedBill.bill_month).format("MMMM YYYY") 
                    : "Monthly";

                rows.push({
                    date,
                    details: `${billLabel} Charges Adjustment from advance`,
                    due: 0,
                    paid: -amtAdvance,
                    type: "adjustment"
                });
            }
        });

        // Sort rows
        const typeOrder: Record<string, number> = {
            "opening": 0,
            "charge": 1,
            "payment": 2,
            "payment_advance": 3,
            "adjustment": 4
        };

        rows.sort((a, b) => {
            const timeA = new Date(a.date).getTime();
            const timeB = new Date(b.date).getTime();
            if (timeA !== timeB) return timeA - timeB;
            return (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99);
        });

        // Compute running balance
        let runningBalance = 0;
        const ledgerRows = rows.map((r) => {
            if (r.type === "opening") {
                runningBalance = r.due;
            } else if (r.type === "charge") {
                runningBalance += r.due;
            } else if (r.type === "payment" || r.type === "payment_advance") {
                runningBalance -= r.paid;
            }
            return {
                ...r,
                balance: runningBalance
            };
        });

        return ledgerRows;
    };

    const handleDownloadPDF = () => {
        const element = document.querySelector(".print-modal-content") as HTMLElement;
        if (!element) return;

        // Show a full-screen loading overlay to mask the modal stretching from the user
        const loadingOverlay = document.createElement("div");
        loadingOverlay.style.position = "fixed";
        loadingOverlay.style.inset = "0";
        loadingOverlay.style.zIndex = "999999";
        loadingOverlay.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
        loadingOverlay.style.display = "flex";
        loadingOverlay.style.flexDirection = "column";
        loadingOverlay.style.alignItems = "center";
        loadingOverlay.style.justifyContent = "center";
        loadingOverlay.style.backdropFilter = "blur(4px)";
        loadingOverlay.innerHTML = `
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <span class="text-gray-800 font-semibold text-lg">Generating PDF Document...</span>
            <span class="text-gray-500 text-sm mt-2">Please wait, this may take a few seconds.</span>
        `;
        document.body.appendChild(loadingOverlay);

        // Save original styles
        const originalMaxHeight = element.style.maxHeight;
        const originalHeight = element.style.height;
        const originalOverflow = element.style.overflow;

        const scrollableBody = element.querySelector(".print-modal-scrollable") as HTMLElement;
        let originalScrollableMaxHeight = "";
        let originalScrollableHeight = "";
        let originalScrollableOverflow = "";
        if (scrollableBody) {
            originalScrollableMaxHeight = scrollableBody.style.maxHeight;
            originalScrollableHeight = scrollableBody.style.height;
            originalScrollableOverflow = scrollableBody.style.overflow;
        }

        // Find and temporarily hide all 'no-print' elements (like close button & footer action buttons)
        const noPrintElements = element.querySelectorAll(".no-print") as NodeListOf<HTMLElement>;
        noPrintElements.forEach((el) => {
            el.style.setProperty("display", "none", "important");
        });

        // Apply temporary style changes for capturing full content in PDF
        element.style.maxHeight = "none";
        element.style.height = "auto";
        element.style.overflow = "visible";

        if (scrollableBody) {
            scrollableBody.style.maxHeight = "none";
            scrollableBody.style.height = "auto";
            scrollableBody.style.overflow = "visible";
        }

        const opt = {
            margin:       [0.5, 0.3, 0.3, 0.3], // Top, Left, Bottom, Right
            filename:     `ledger_${timelineData?.connection?.connection_number || "consumer"}_${new Date().toISOString().split("T")[0]}.pdf`,
            image:        { type: "jpeg", quality: 1 },
            html2canvas:  { 
                scale: 2, 
                useCORS: true,
                logging: false,
                letterRendering: true
            },
            jsPDF:        { unit: "in", format: "a4", orientation: "portrait" },
            pagebreak:    { mode: ["css", "legacy"] }
        };

        // Generate and download PDF
        // @ts-ignore
        html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf: any) => {
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 2; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(8);
                pdf.setTextColor(100);
                
                const consumerName = timelineData?.connection?.consumer_name || "";
                const connNo = `Connection No: ${timelineData?.connection?.connection_number || ""}`;
                
                // Draw consumer name on left-top (x = 0.3, y = 0.35 inches)
                pdf.text(consumerName, 0.3, 0.35);
                
                // Draw connection number on right-top (x = 7.97, y = 0.35 inches)
                pdf.text(connNo, 7.97, 0.35, { align: 'right' });
                
                // Draw a thin line under the header (x1, y1, x2, y2)
                pdf.setDrawColor(220);
                pdf.setLineWidth(0.01);
                pdf.line(0.3, 0.42, 7.97, 0.42);
            }
            // Trigger native download from jsPDF instance
            pdf.save(`ledger_${timelineData?.connection?.connection_number || "consumer"}.pdf`);
        }).then(() => {
            // Restore original styles
            element.style.maxHeight = originalMaxHeight;
            element.style.height = originalHeight;
            element.style.overflow = originalOverflow;

            if (scrollableBody) {
                scrollableBody.style.maxHeight = originalScrollableMaxHeight;
                scrollableBody.style.height = originalScrollableHeight;
                scrollableBody.style.overflow = originalScrollableOverflow;
            }

            // Restore hidden elements
            noPrintElements.forEach((el) => {
                el.style.removeProperty("display");
            });

            // Remove loading overlay
            if (loadingOverlay.parentNode) {
                document.body.removeChild(loadingOverlay);
            }
        }).catch((err: any) => {
            console.error("PDF generation error:", err);
            // Restore original styles on error too
            element.style.maxHeight = originalMaxHeight;
            element.style.height = originalHeight;
            element.style.overflow = originalOverflow;

            if (scrollableBody) {
                scrollableBody.style.maxHeight = originalScrollableMaxHeight;
                scrollableBody.style.height = originalScrollableHeight;
                scrollableBody.style.overflow = originalScrollableOverflow;
            }

            // Restore hidden elements
            noPrintElements.forEach((el) => {
                el.style.removeProperty("display");
            });

            // Remove loading overlay
            if (loadingOverlay.parentNode) {
                document.body.removeChild(loadingOverlay);
            }
        });
    };

    const handleViewLedgerTimeline = async (item: any) => {
        setTimelineLoading(true);
        setTimelineData(null);
        setLedgerRows([]);
        setOpenTimelinePopup(true);
        try {
            const res = await axiosInstance.post(API_URLS.customerLedgerTimeline, { connection_id: item.id });
            const data = res.data?.data;
            setTimelineData(data);
            if (data) {
                const rows = data.ledger_rows && Array.isArray(data.ledger_rows) && data.ledger_rows.length > 0
                    ? data.ledger_rows
                    : buildLedgerRows(data);
                setLedgerRows(rows);
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to fetch ledger timeline");
            setOpenTimelinePopup(false);
        } finally {
            setTimelineLoading(false);
        }
    };

    // ─── Initial Load ────────────────────────────────────────────────────────
    useEffect(() => {
        if (isMunicipalityAdmin && municipalityIdFromStorage) {
            setSelectedMunicipality(String(municipalityIdFromStorage));
            fetchBoroughs(Number(municipalityIdFromStorage));
        } else {
            const fetchMunicipalities = async () => {
                try {
                    const res = await axiosInstance.post(API_URLS.MunicipalityList, { limit: 50, page: 1 });
                    setMunicipalities(res.data?.data?.data || []);
                } catch { toast.error("Failed to fetch municipalities"); }
            };
            fetchMunicipalities();
        }
    }, []);

    useEffect(() => {
        if (isProgressOpen || openDuePopup || openTimelinePopup) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [isProgressOpen, openDuePopup, openTimelinePopup]);

    // ─── Render Helpers ──────────────────────────────────────────────────────
    const municipalityOptions = municipalities.map((m) => ({ value: m.primary_id, label: m.municipality_name }));
    const boroughOptions = boroughs.map((b) => ({ value: b.borough_primary_id, label: b.borough_name }));
    const wardOptions = wards.map((w) => ({ value: w.ward_primary_id, label: w.ward_no }));
    const streetOptions = streets.map((s) => ({ value: s.street_primary_id, label: s.street_name }));

    const startIndex = (page - 1) * limit + 1;
    const endIndex = Math.min(page * limit, totalRecords);

    // ════════════════════════════════════════════════════════════════════════
    //  RENDER  —  Demand Draft design language applied to Customer Ledger
    // ════════════════════════════════════════════════════════════════════════
    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-7xl mx-auto">

                {/* ── Header (Demand Draft style) ── */}
                <div className="mb-8">
                    <div className="flex justify-between items-start flex-wrap gap-4">
                        <div className="flex flex-col gap-2">
                            <h1 className="text-3xl font-bold text-gray-800 border-b-4 border-green-500 pb-2 inline-block">
                                Customer Ledger
                            </h1>
                            {isMunicipalityAdmin && (
                                <div className="text-sm text-gray-600">
                                    Municipality: <span className="font-semibold text-green-700">{municipalityNameFromStorage}</span>
                                </div>
                            )}
                        </div>

                        {/*https://wnn4x81g-3001.inc1.devtunnels.ms/ View Report Progress button */}
                        <button
                            onClick={openProgressPopup}
                            disabled={!isLocationReady()}
                            className={`px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg transition-all flex items-center gap-2 ${
                                !isLocationReady() ? "opacity-50 cursor-not-allowed" : "hover:bg-purple-700"
                            }`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Report Progress
                        </button>
                    </div>
                </div>

                {/* ── Location Selection (Demand Draft style card) ── */}
                <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {!isMunicipalityAdmin && (
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Municipality <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect value={selectedMunicipality} onChange={handleMunicipalityChange} options={municipalityOptions} placeholder="Select Municipality" />
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Borough <span className="text-red-500">*</span></label>
                            <CustomSelect value={selectedBorough} onChange={handleBoroughChange} options={boroughOptions} placeholder={loadingBorough ? "Loading..." : "Select Borough"} disabled={!selectedMunicipality || loadingBorough} />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Ward <span className="text-red-500">*</span></label>
                            <CustomSelect value={selectedWard} onChange={handleWardChange} options={wardOptions} placeholder={loadingWard ? "Loading..." : "Select Ward"} disabled={!selectedBorough || loadingWard} />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Street <span className="text-red-500">*</span></label>
                            <CustomSelect value={selectedStreet} onChange={handleStreetChange} options={streetOptions} placeholder={loadingStreet ? "Loading..." : "Select Street"} disabled={!selectedWard || loadingStreet} />
                        </div>
                    </div>
                </div>

                {/* ── Ledger Table Section (Demand Draft style card) ── */}
                {selectedStreet && (
                    <div className="bg-white rounded-lg shadow-lg p-6">

                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">

  <div className="flex flex-wrap gap-3 items-end">
    
    <div className="flex flex-col">
      <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
        Start Date
      </label>
      <input
        type="date"
        value={startDate}
        max={getToday()}
        onChange={(e) => handleStartDateChange(e.target.value)}
        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
      />
    </div>

    <div className="flex flex-col">
      <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
        End Date
      </label>
      <input
        type="date"
        value={endDate}
        min={startDate}
        max={getToday()}
        onChange={(e) => handleEndDateChange(e.target.value)}
        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
      />
    </div>

    <button
      onClick={handleReset}
      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition"
    >
      ↺ Reset
    </button>
  </div>

  <div className="flex flex-wrap gap-3 items-center lg:justify-end">

    {/* Search */}
    <div className="relative flex items-center">
      <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
      </svg>
      <input
        type="text"
        placeholder="Search consumer..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 w-56 text-sm"
      />
    </div>

    {/* Generate Selected */}
    <button
      onClick={() => generateReport("selected")}
      disabled={selectedCount === 0 || loadingGenerateSelected || loadingGenerateAll}
      className={`px-5 py-2 text-white font-semibold rounded-lg transition-all flex items-center gap-2 ${
        selectedCount === 0 || loadingGenerateSelected || loadingGenerateAll
          ? "bg-gray-300 text-gray-400 cursor-not-allowed"
          : "bg-green-600 hover:bg-green-700 active:scale-95"
      }`}
    >
      {loadingGenerateSelected ? "Generating..." : `Generate (${selectedCount})`}
    </button>

    {/* Generate All */}
    <button
      onClick={() => generateReport("all")}
      disabled={loadingGenerateAll || loadingGenerateSelected}
      className={`px-5 py-2 text-white font-semibold rounded-lg transition-all flex items-center gap-2 ${
        loadingGenerateAll || loadingGenerateSelected
          ? "bg-gray-300 text-gray-400 cursor-not-allowed"
          : "bg-blue-600 hover:bg-blue-700 active:scale-95"
      }`}
    >
      {loadingGenerateAll ? "Generating All..." : "Generate All"}
    </button>

  </div>


</div>
        

                        {dateError && (
                            <p className="text-red-500 text-xs mb-3 flex items-center gap-1">
                                <span>⚠️</span> {dateError}
                            </p>
                        )}

                        {/* Showing entries info */}
                        {totalRecords > 0 && (
                            <div className="text-xs text-gray-500 mb-3">
                                Showing <span className="font-semibold text-gray-700">{startIndex}–{endIndex}</span> of{" "}
                                <span className="font-semibold text-gray-700">{totalRecords}</span> entries
                            </div>
                        )}

                        {/* Table */}
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 border-b w-12">
                                            <input type="checkbox" checked={selectAllChecked} onChange={toggleSelectAll}
                                                className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                                        </th>
                                        <th className="px-4 py-3 border-b text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                                        <th className="px-4 py-3 border-b text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Consumer Name</th>
                                        <th className="px-4 py-3 border-b text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Connection No</th>
                                        <th className="px-4 py-3 border-b text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="px-4 py-3 border-b text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Monthly Rate</th>
                                        <th className="px-4 py-3 border-b text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Opening Balance</th>
                                        <th className="px-4 py-3 border-b text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Deposited</th>
                                        <th className="px-4 py-3 border-b text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Amount</th>
                                        <th className="px-4 py-3 border-b text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Advance Amount</th>
                                        <th className="px-4 py-3 border-b text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Months</th>
                                        <th className="px-4 py-3 border-b text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingTable ? (
                                        <tr>
                                            <td colSpan={12} className="text-center py-10 text-gray-500">
                                                <div className="flex justify-center items-center gap-2">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                                                    <span>Loading records...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : ledgerData.length === 0 ? (
                                        <tr>
                                            <td colSpan={12} className="text-center py-10 text-gray-500">No customer data found</td>
                                        </tr>
                                    ) : (
                                        ledgerData.map((item, idx) => (
                                            <tr
                                                key={item.id}
                                                className={`transition-colors ${selectedIds.includes(item.id) ? "bg-green-50 hover:bg-green-100" : "hover:bg-gray-50"} ${idx % 2 !== 0 ? "bg-gray-50/40" : ""}`}
                                            >
                                                <td className="px-4 py-3 border-b text-center">
                                                    <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)}
                                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                                                </td>
                                                <td className="px-4 py-3 border-b text-center text-gray-400 text-xs">
                                                    {(page - 1) * limit + idx + 1}
                                                </td>
                                                <td className="px-4 py-3 border-b text-sm font-medium text-gray-800">{item.consumer_name}</td>
                                                <td className="px-4 py-3 border-b text-sm text-gray-500 font-mono">{item.connection_number}</td>
                                                <td className="px-4 py-3 border-b text-sm text-gray-500">{item.consumer_category_name || "-"}</td>
                                                <td className="px-4 py-3 border-b text-sm text-right text-gray-700 font-semibold">
                                                    {item.consumer_monthly_rate ? `₹ ${Number(item.consumer_monthly_rate).toLocaleString("en-IN")}` : "-"}
                                                </td>
                                                <td className="px-4 py-3 border-b text-sm text-right text-gray-700">
                                                    ₹ {(item.opening_balance_left_to_pay || 0).toLocaleString("en-IN")}
                                                </td>
                                                <td className="px-4 py-3 border-b text-sm text-right">
                                                    <span className="text-emerald-600 font-medium">₹ {(item.amount_deposited || 0).toLocaleString("en-IN")}</span>
                                                </td>
                                                <td className="px-4 py-3 border-b text-sm text-right">
                                                    {item.total_due_amount > 0
                                                        ? <span className="text-red-600 font-semibold">₹ {(item.total_due_amount || 0).toLocaleString("en-IN")}</span>
                                                        : <span className="text-gray-400">₹ 0</span>
                                                    }
                                                </td>
                                                <td className="px-4 py-3 border-b text-sm text-right">
                                                    {item?.advance_amount && item.advance_amount > 0
                                                        ? <span className="text-green-600 font-semibold">₹ {(item.advance_amount || 0).toLocaleString("en-IN")}</span>
                                                        : <span className="text-gray-400">₹ 0</span>
                                                    }
                                                </td>
                                                <td className="px-4 py-3 border-b text-center">
                                                    {(() => {
                                                        const unpaidCount = (item.due_months || []).filter((m: any) => !m.is_paid).length;
                                                        return unpaidCount > 0 ? (
                                                            <button
                                                                onClick={() => handleDueClick(item)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 border border-red-100 rounded-full text-xs font-medium hover:bg-red-100 transition"
                                                            >
                                                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse inline-block" />
                                                                {unpaidCount} month{unpaidCount !== 1 ? "s" : ""}
                                                            </button>
                                                        ) : <span className="text-gray-300">-</span>;
                                                    })()}
                                                </td>
                                                <td className="px-4 py-3 border-b text-center">
                                                    <button
                                                        onClick={() => handleViewLedgerTimeline(item)}
                                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition flex items-center gap-1 mx-auto"
                                                    >
                                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                                        </svg>
                                                        View Ledger
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>



                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center mt-6">
                                <Pagination
                                    count={totalPages} page={page}
                                    onChange={(_, value) => { setPage(value); fetchLedgerData(value, debouncedSearch, startDate, endDate); }}
                                    color="primary" size="small"
                                />
                            </div>
                        )}

                        {totalRecords > 0 && (
                            <div className="text-center text-sm text-gray-500 mt-3">
                                Showing {startIndex} to {endIndex} of {totalRecords} entries
                                {totalPages > 1 && ` | Page ${page} of ${totalPages}`}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                Due Months Modal  —  Demand Draft popup style
            ══════════════════════════════════════════════════════════════ */}
            {openDuePopup && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setOpenDuePopup(false); }}
                >
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
                        {/* Header */}
                        <div className="px-6 pt-5 pb-4 flex justify-between items-start" style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f4c75 100%)" }}>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                                    <span className="text-xs font-semibold text-blue-300 uppercase tracking-widest">Due Details</span>
                                </div>
                                <h3 className="text-lg font-bold text-white leading-tight">{selectedCustomer?.consumer_name}</h3>
                                <p className="text-sm text-slate-400 mt-0.5 font-mono">{selectedCustomer?.connection_number}</p>
                            </div>
                            <button onClick={() => setOpenDuePopup(false)} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition text-sm">✕</button>
                        </div>

                        {/* Summary strip */}
                        <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center justify-between">
                            <span className="text-xs font-semibold text-red-500 uppercase tracking-wide">
                                {selectedDueMonths.length} overdue month{selectedDueMonths.length !== 1 ? "s" : ""}
                            </span>
                            <span className="text-sm font-bold text-red-600">
                                Total: ₹ {selectedDueMonths.reduce((s, d) => s + (d.bill_amount || 0), 0).toLocaleString("en-IN")}
                            </span>
                        </div>

                        {/* Body */}
                        <div className="overflow-y-auto" style={{ maxHeight: "320px" }}>
                            {selectedDueMonths.length === 0 ? (
                                <div className="py-10 text-center text-gray-400 text-sm">No due months found</div>
                            ) : selectedDueMonths.map((d, i) => (
                                <div key={i} className="flex justify-between items-center px-6 py-3 border-b border-gray-50 hover:bg-gray-50 transition">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center text-red-500 text-xs font-bold flex-shrink-0">{i + 1}</div>
                                        <span className="text-sm font-medium text-gray-700">{d.bill_month}</span>
                                    </div>
                                    <span className="text-sm font-bold text-red-600">₹ {(d.bill_amount || 0).toLocaleString("en-IN")}</span>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                            <button onClick={() => setOpenDuePopup(false)} className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-semibold transition">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Progress Popup  —  Demand Draft progress popup style
            ══════════════════════════════════════════════════════════════ */}
            {isProgressOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setIsProgressOpen(false); }}
                >
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden" style={{ height: "80vh", maxHeight: "800px", minHeight: "500px" }}>

                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">Report Generation Progress</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Borough: {boroughs.find((b) => b.borough_primary_id === selectedBorough)?.borough_name || "-"} |&nbsp;
                                        Ward: {wards.find((w) => w.ward_primary_id === selectedWard)?.ward_no || "-"} |&nbsp;
                                        Street: {streets.find((s) => s.street_primary_id === selectedStreet)?.street_name || "-"}
                                    </p>
                                </div>
                                <button onClick={() => setIsProgressOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Toolbar */}
                        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50">
                            <div className="flex items-center gap-4">
                                {statusTotal > 0 && (
                                    <p className="text-xs text-gray-500">
                                        Total records: <span className="font-semibold text-gray-700">{statusTotal}</span>
                                    </p>
                                )}
                                {/* Status legend */}
                                <div className="flex items-center gap-3 text-xs text-gray-400">
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full inline-block" />Completed</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-400 rounded-full inline-block" />Processing</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full inline-block" />Failed</span>
                                </div>
                            </div>
                            <button
                                onClick={() => fetchReportStatus(statusPage)}
                                disabled={statusLoading}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-sm text-xs font-medium"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {statusLoading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>

                        {/* Table area */}
                        <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 pb-4">
                            {statusLoading ? (
                                <div className="flex justify-center items-center h-full min-h-[300px]">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
                                </div>
                            ) : reportStatusList.length === 0 ? (
                                <div className="text-center text-gray-500 flex flex-col items-center justify-center h-full min-h-[300px]">
                                    <svg className="w-14 h-14 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="font-medium">No reports generated yet</p>
                                    <p className="text-sm mt-1 text-gray-400">Generate reports from the ledger table above</p>
                                </div>
                            ) : (
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                                        <tr>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ledger Number</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Generated On</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Financial Year</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Est. Completion</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {reportStatusList.map((report, index) => (
                                            <tr key={report.id} className={`hover:bg-gray-50 transition-colors ${index % 2 !== 0 ? "bg-gray-50/40" : "bg-white"}`}>
                                                <td className="px-5 py-3">
                                                    <AllotmentCell value={report.ledger_allotment_number} />
                                                </td>
                                                <td className="px-5 py-3 text-gray-500 text-xs">{formatDateTime(report.generated_on)}</td>
<td className="px-5 py-3 text-gray-600 text-xs font-mono break-all">
  {report.fy_selected || "-"}
</td>                                                <td className="px-5 py-3 text-gray-500 text-xs">
                                                    {report.estimated_time_of_completion === "Calculating..." ? (
                                                        <div className="flex items-center gap-1.5 text-cyan-600">
                                                            <div className="w-3 h-3 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                                                            <span>Calculating...</span>
                                                        </div>
                                                    ) : report.estimated_time_of_completion || "-"}
                                                </td>
                                                <td className="px-5 py-3">
                                                    {report.status === "completed" ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Completed
                                                        </span>
                                                    ) : report.status === "failed" ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full" /> Failed
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                                            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" /> Processing
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3">
                                                    {report.status === "completed" ? (
                                                        <div className="relative inline-block group">
                                                            <button
                                                                onClick={() => { if (!report.is_downloaded) downloadReport(report.ledger_allotment_number); }}
                                                                disabled={report.is_downloaded || downloadingFile === report.ledger_allotment_number}
                                                                title={report.is_downloaded ? "File has already been downloaded" : "Download PDF"}
                                                                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 text-sm font-medium ${
                                                                    report.is_downloaded
                                                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        : "bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                }`}
                                                            >
                                                                {downloadingFile === report.ledger_allotment_number ? (
                                                                    <>
                                                                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                                        </svg>
                                                                        Downloading...
                                                                    </>
                                                                ) : report.is_downloaded ? (
                                                                    <>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        Downloaded
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                        </svg>
                                                                        Download
                                                                    </>
                                                                )}
                                                            </button>
                                                            {report.is_downloaded && (
                                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-20 shadow-lg">
                                                                    File has already been downloaded
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : report.status === "failed" ? (
<div className="relative inline-block group">
        <span className="text-gray-500 cursor-pointer text-lg 
                         hover:text-gray-700 hover:scale-110 transition-all duration-200">
            <i className="ri-information-fill"></i>
        </span>

        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 
                        px-4 py-2 bg-gray-800 text-white text-sm rounded-lg 
                        whitespace-nowrap opacity-0 group-hover:opacity-100 
                        pointer-events-none transition-all duration-200 shadow-xl z-20">
            PDF generation failed. Please try again.

            {/* Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 
                            border-4 border-transparent border-t-gray-800" />
        </div>
    </div>
                                                   ) : (
                                                        <span className="text-xs text-yellow-600 flex items-center gap-1.5">
                                                            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                            </svg>
                                                            Processing...
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer: pagination */}
                        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0 flex items-center justify-between bg-gray-50/50 min-h-[57px]">
                            <p className="text-xs text-gray-400">
                                {statusTotal > 0 && (
                                    <>
                                        Showing {(statusPage - 1) * statusLimit + 1}–{Math.min(statusPage * statusLimit, statusTotal)} of {statusTotal}
                                        {statusLastPage > 1 && ` | Page ${statusPage} of ${statusLastPage}`}
                                    </>
                                )}
                            </p>
                            <div className="flex justify-center mt-4">
                            <Pagination
                                count={statusLastPage} 
                                page={statusPage}
                                onChange={(_, val) => { setStatusPage(val); fetchReportStatus(val); }}
                                color="primary" 
                                disabled={statusLoading}
                                showFirstButton 
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                                size="small"
                            />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Consumer Ledger Timeline Modal
            ══════════════════════════════════════════════════════════════ */}
            {openTimelinePopup && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm print-modal-overlay"
                    onClick={(e) => { if (e.target === e.currentTarget) setOpenTimelinePopup(false); }}
                >
                    <style dangerouslySetInnerHTML={
                        {__html: `
                        /* Screen styles (hidden by default on screen) */
                        .print-fixed-header {
                            display: none;
                        }
                        @media print {
                            /* Hide layout elements completely to remove them from layout flow */
                            .sidebar-nav,
                            .header-bar,
                            .Toastify,
                            main > div:not(.min-h-screen),
                            .min-h-screen.bg-gray-100 > div:first-child {
                                display: none !important;
                            }
                            
                            /* Reset parent layouts to avoid empty margins/paddings and sidebar shift */
                            body, html, #root, 
                            .min-h-screen, 
                            .main-content, 
                            main {
                                margin: 0 !important;
                                padding: 0 !important;
                                background: #f8fafc !important;
                                height: auto !important;
                                min-height: 0 !important;
                                width: 100% !important;
                                overflow: visible !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }

                            /* Define page margins */
                            @page {
                                size: A4;
                                background-color: #ebebec;
                                margin: 0 !important;
                                padding-top: 10px;
                            }
                            @page :first {
                                margin-top: 0mm;
                            }
                            body {
                                padding: 0 !important; 
                                margin: 0 !important;
                                background: #f8fafc !important; 
                            }

                            /* Modal Overlay Styling for print */
                            .print-modal-overlay {
                                position: absolute !important;
                                left: 0 !important;
                                top: 0 !important;
                                width: 100% !important;
                                height: auto !important;
                                background: #f8fafc !important;
                                padding: 0 !important;
                                margin: 0 !important;
                                display: block !important;
                                overflow: visible !important;
                                box-sizing: border-box !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            
                            /* Modal Card Content styling */
                            .print-modal-content {
                                display: block !important; /* Override flex container behavior for correct page breaking */
                                max-height: none !important;
                                height: auto !important;
                                border: none !important;
                                box-shadow: none !important;
                                width: 100% !important;
                                max-width: none !important;
                                border-radius: 1rem !important;
                                position: relative !important;
                                left: 0 !important;
                                top: 0 !important;
                                background: white !important;
                                overflow: hidden !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            
                            /* Force header gradient background in print */
                            .print-modal-content > div:first-child {
                                background: linear-gradient(135deg, #1e293b 0%, #0f4c75 100%) !important;
                                color: white !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                display: block !important;
                            }
                            
                            /* Body of modal - allow table to span multiple pages */
                            .print-modal-scrollable {
                                display: block !important; /* Override flex-1 layout */
                                overflow: visible !important;
                                max-height: none !important;
                                height: auto !important;
                                padding: 6mm !important;
                            }

                            /* Prevent table container borders/shadows from rendering broken split boxes across pages */
                            .print-table-wrapper {
                                border: none !important;
                                box-shadow: none !important;
                                border-radius: 0 !important;
                                overflow: visible !important;
                            }

                            /* Ensure table spans full width cleanly in block flow */
                            .print-table-wrapper table {
                                border-collapse: collapse !important;
                                width: 100% !important;
                            }

                            /* Print cell bottom borders clearly */
                            .print-table-wrapper th,
                            .print-table-wrapper td {
                                border-bottom: 1px solid #e2e8f0 !important;
                            }

                            /* Prevent a single table row from splitting mid-row across pages */
                            .print-table-wrapper tr {
                                page-break-inside: avoid !important;
                                break-inside: avoid !important;
                            }

                            /* Print Header Style for Page 2+ */
                            .print-fixed-header {
                                display: block !important;
                                position: fixed !important;
                                top: -15mm !important;
                                left: 15mm !important;
                                right: 15mm !important;
                                height: 10mm !important;
                                z-index: 1000 !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            
                            /* Hide action buttons and close triggers */
                            .no-print {
                                display: none !important;
                            }
                        }
                    `}} />
                    {/* Fixed header for print (Page 2+) */}
                    <div className="print-fixed-header hidden print:block">
                        <div className="flex justify-between items-center w-full border-b border-gray-300 pb-1 text-[10px] text-gray-500 font-semibold uppercase tracking-wider">
                            <span>{timelineData?.connection?.consumer_name}</span>
                            <span>Connection No: {timelineData?.connection?.connection_number}</span>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col print-modal-content" style={{ maxHeight: "90vh" }}>
                        
                        {/* Header */}
                        <div className="px-6 py-5 flex justify-between items-start flex-shrink-0" style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f4c75 100%)" }}>
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                    Consumer Ledger Details
                                </h3>
                                <p className="text-sm text-slate-300 mt-1">Detailed history of dues, payments, advance collections, and adjustments</p>
                            </div>
                            <button onClick={() => setOpenTimelinePopup(false)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition text-lg font-semibold no-print">✕</button>
                        </div>

                        {/* Loading / Body */}
                        {timelineLoading ? (
                            <div className="flex flex-col items-center justify-center py-20 flex-1">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
                                <span className="text-gray-500 font-medium">Fetching consumer ledger details...</span>
                            </div>
                        ) : !timelineData ? (
                            <div className="py-20 text-center text-gray-500 flex-1">
                                No ledger details found or failed to load.
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 print-modal-scrollable">
                                
                                {/* Identification Card */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Consumer Profile</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Consumer Name</span>
                                            <span className="font-semibold text-gray-800 text-sm">{timelineData.connection.consumer_name}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Connection Number</span>
                                            <span className="font-mono text-gray-800 text-sm font-semibold">{timelineData.connection.connection_number}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Holding Number</span>
                                            <span className="text-gray-800 text-sm">{timelineData.connection.holding_number || "-"}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Phone Number</span>
                                            <span className="text-gray-800 text-sm">{timelineData.connection.phone_number || "-"}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Category</span>
                                            <span className="text-gray-800 text-sm">{timelineData.connection.consumer_category_name || "-"}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Rate</span>
                                            <span className="text-gray-800 text-sm font-semibold">
                                                {timelineData.connection.rate ? `₹ ${Number(timelineData.connection.rate).toLocaleString("en-IN")}` : "-"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Ferule Size</span>
                                            <span className="text-gray-800 text-sm">{timelineData.connection.ferule_size ? `${timelineData.connection.ferule_size}"` : "-"}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Ward No</span>
                                            <span className="text-gray-800 text-sm">{timelineData.connection.ward_name || "-"}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Current Advance Balance</span>
                                            <span className={`text-sm font-bold ${Number(timelineData.connection.advance_amount) > 0 ? "text-green-600" : "text-gray-400"}`}>
                                                ₹ {Number(timelineData.connection.advance_amount || 0).toLocaleString("en-IN")}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Opening Balance</span>
                                            <span className="text-gray-800 text-sm font-semibold text-red-600">
                                                ₹ {(ledgerRows.find((r: any) => r.type === "opening")?.due || (Number(timelineData.connection.opening_due_amount) || 0)).toLocaleString("en-IN")}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="block text-xs text-gray-500 font-medium mb-0.5">Opening Balance Date</span>
                                            <span className="text-gray-800 text-sm font-medium">
                                                {timelineData.connection.connection_start_date
                                                    ? moment(timelineData.connection.connection_start_date).format("DD-MM-YYYY")
                                                    : timelineData.connection.created_at
                                                        ? moment(timelineData.connection.created_at).format("DD-MM-YYYY")
                                                        : "-"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Ledger Table */}
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Ledger Entries</h4>
                                    <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm print-table-wrapper">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-slate-100 border-b border-gray-200 text-gray-600 font-semibold text-xs uppercase tracking-wider">
                                                    <th className="py-3 px-4 text-left font-semibold">Date</th>
                                                    <th className="py-3 px-4 text-left font-semibold">Transaction Details</th>
                                                    <th className="py-3 px-4 text-right font-semibold">Due Amount</th>
                                                    <th className="py-3 px-4 text-right font-semibold">Paid Amount</th>
                                                    <th className="py-3 px-4 text-right font-semibold">Balance</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 bg-white">
                                                {ledgerRows.map((row, index) => {
                                                    const formattedDate = moment(row.date).format("DD-MM-YYYY");
                                                    return (
                                                        <tr key={index} className="hover:bg-slate-50 transition-colors">
                                                            <td className="py-3.5 px-4 text-gray-600 text-xs font-mono">{formattedDate}</td>
                                                            <td className="py-3.5 px-4 font-medium text-gray-800 text-xs">{row.details}</td>
                                                            <td className="py-3.5 px-4 text-right text-gray-700 font-semibold">
                                                                {row.due > 0 ? `₹ ${row.due.toLocaleString("en-IN")}` : "-"}
                                                            </td>
                                                            <td className="py-3.5 px-4 text-right font-semibold text-emerald-600">
                                                                {row.paid > 0 ? `₹ ${row.paid.toLocaleString("en-IN")}` : "-"}
                                                            </td>
                                                            <td className={`py-3.5 px-4 text-right font-bold ${
                                                                row.balance < 0 ? "text-green-600" : row.balance > 0 ? "text-red-500" : "text-gray-400"
                                                            }`}>
                                                                {row.balance < 0 
                                                                    ? `₹ ${Math.abs(row.balance).toLocaleString("en-IN")} Cr`
                                                                    : row.balance > 0
                                                                        ? `₹ ${row.balance.toLocaleString("en-IN")} Dr`
                                                                        : `₹ 0`
                                                                }
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center flex-shrink-0 no-print">
                            <button
                                onClick={handleDownloadPDF}
                                disabled={timelineLoading || !timelineData}
                                className="px-4 py-2 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition rounded-xl text-sm font-semibold flex items-center gap-1.5"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                Print Ledger
                            </button> 
                            <button onClick={() => setOpenTimelinePopup(false)} className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-semibold transition">Close</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default CustomerLedger;